import { Suspense, JSX } from "react";
import IbssRouter from "./Components/Navigation/Router/IbssRouter";
import { Helmet } from "react-helmet";
import { MsalProvider } from "@azure/msal-react";
import { appContext } from "./AppContext";
import LegacyApiClient, { IActiveNotification } from "./Providers.Api/LegacyApiClient";
import { ThemeId } from "./Common/CustomTheme";
import { IbssComponent } from "./Components/Core/BaseComponent/IbssComponent";
import { LogOutReason } from "./Common/Authentication";
import Labels from "./Data.Labels/Labels";
import ApiMessages from "./Data.Labels/ApiMessages";
import { Localisation } from "@iconics/localisation";
import Translation from "@monza/toolkit/common/localization/translation";
import AssetTranslation from "@monza/asset-controls/localization/translation";
import { components } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import UnitedKingdomIcon from "./Components/Icons/Flags/UnitedKingdom";
import JapanIcon from "./Components/Icons/Flags/Japan";
import { Core } from "@iconics/core";

class App extends IbssComponent<IProps, IState>
{
    private appContext = appContext();
    private get local() { return appContext().localStorageProvider; }
    private resizeHandlerId = '';

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            themeId: 'konjo',
            ready: false,
        };
    }

    private notificationTimerId: NodeJS.Timeout | null = null;

    public async componentDidMount(): Promise<void>
    {
        this.appContext.useDefaultSettingsProvider();
        await this.appContext.initaliseSettings();

        // initialise localisation
        appContext().localisation = new Localisation();
        appContext().localisation.useDateTimeExtensions().useNumberExtensions();

        const languageOptions = await appContext().localisation
            .createLanguageOptions()
            .useLanguages(
                {
                    code: appContext().localisation.terms,
                    text: () => appContext().labels.Platform_LanguageMenu_Terms,
                    icon: <SerendieSymbol name='type' />,
                },
                {
                    code: 'en-GB',
                    text: () => appContext().labels.Platform_LanguageMenu_English,
                    icon: <UnitedKingdomIcon />,
                },
                {
                    code: 'ja',
                    text: () => appContext().labels.Platform_LanguageMenu_Japanese,
                    icon: <JapanIcon />,
                },
            )
            .setDefaultLanguage('en-GB')
            .useDefaultLanguageProvider()
            .build();

        const labelsBuilder = await appContext().localisation
            .createLabelBuilder(new Labels())
            .setLangugeOptions(languageOptions)
            .addLabelsProvider(i => import(`./Data.Labels/${i}/Labels.json`))
            .addLabelsProvider(i => import(`./Data.Labels/en-GB/Labels.json`))
            .usePoEditorLabelsParser()
            .autoMap()
            .build();

        const apiMessagesBuilder = await appContext().localisation
            .createLabelBuilder(new ApiMessages())
            .setLangugeOptions(languageOptions)
            .addLabelsProvider(i => import(`./Data.Labels/${i}/ApiMessages.json`))
            .addLabelsProvider(i => import(`./Data.Labels/en-GB/ApiMessages.json`))
            .usePoEditorLabelsParser()
            .autoMap()
            .build();

        appContext().language = languageOptions;
        appContext().labels = labelsBuilder.labels;
        appContext().apiMessages = apiMessagesBuilder.labels;
        Translation.currentLanguage = languageOptions.current;
        AssetTranslation.currentLanguage = languageOptions.current;

        // initialise core
        appContext().core = new Core();

        appContext().core
            .configureValidation({
                invalidFieldLabel: labelsBuilder.labels.Platform_Invalid_Field,
                isRequiredLabel: labelsBuilder.labels.Platform_Common_MandatoryField,
                mustBeNumberLabel: labelsBuilder.labels.Platform_Common_MustBeNumber,
                mustBeEqualToLabel: labelsBuilder.labels.Platform_Common_MustBeEqualTo,
                mustBeGreaterThanLabel: labelsBuilder.labels.Platform_Common_MustBeGreaterThan,
                mustBeMaxLengthLabel: labelsBuilder.labels.Platform_Common_MustBeMaxLength,
                mustBeValidEmailLabel: labelsBuilder.labels.Platform_Common_InvalidEmailFormat,
            });

        // initialise components
        const labels = appContext().labels;

        components
            .initialise({
                platformApiV2Url: appContext().settings.platformApi,
                webEntryApiV1Url: appContext().settings.dataEntryApi,
                isDevMode: appContext().settings.devMode,
            })
            .configureDeleteButton({
                buttonLabel: labels.Platform_CommonAction_Delete,
                dialogTitle: labels.Platform_ConfirmDelete_Title,
                dialogMessage: labels.Platform_ConfirmDelete_Message,
                dialogConfirmDeleteLabel: labels.Platform_CommonAction_Delete,
            })
            .configureAlertDialog({
                okButtonLabel: labels.Platform_CommonAction_Ok,
            })
            .configureConfirmDialog({
                okButtonLabel: labels.Platform_CommonAction_Ok,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureLanguageDialog({
                title: labels.Platform_LanguageMenu_Title,
                subTitle: labels.Platform_LanguageMenu_SubTitle,
                languageInputLabel: labels.Platform_LanguageMenu_Language,
                okButtonLabel: labels.Platform_CommonAction_Save,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureFileDropZone({
                prompt: labels.Platform_AddFile_DropZone_Message,
                selectButtonLabel: labels.Platform_AddFile_SelectFile,
                resetButtonLabel: labels.FileDropzone_Reset,
            })
            .configureMediaSelector({
                selectButtonLabel: labels.Platform_AddSelectImage,
                dialogTitle: labels.Platform_ImageSelection,
                duplicateImageError: labels.Platform_ImageNameExistsError,
                selectedImageLabel: labels.Platform_SelectedImage,
                okButtonLabel: labels.Platform_CommonAction_Ok,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureMultilingualTextField({
                unknownLanguageLabel: labels.Platform_LanguageMenu_Unknown,
                dialogTitle: labels.Platform_MultilingualTextField_Title,
                dialogSubTitle: labels.Platform_MultilingualTextField_SubTitle,
                okButtonLabel: labels.Platform_CommonAction_Save,
                cancelButtonLabel: labels.Platform_CommonAction_Cancel,
            })
            .configureUserPicker({

                label: labels.HubLabelNameOrEmailLabel,
                noUserSelectedLabel: labels.funcNoUserSelected_S,
            })
            .addResizeHandler(
                size => this.appContext.state.set({ screenSize: size }),
                id => this.resizeHandlerId = id
            );

        // register helpers
        this.appContext.useDefaultCache();
        this.appContext.cache.startGarbageCollection();

        // register providers
        this.appContext.useDefaultLocalStorageProvider();
        this.appContext.useDefaultSessionStorageProvider();
        this.appContext.useDefaultApiCache();
        this.appContext.useDefaultApiClient();
        this.appContext.useDefaultWebEntryApiClientV1();
        this.appContext.useDefaultFadxPlatformApiClientV2();
        this.appContext.useDefaultMsalProvider();
        this.appContext.useDefaultStaticDataProvider(labelsBuilder.labels);

        // initialise MSAL
        const msal = appContext().msalProvider;
        if (msal.clientId && msal.tenantDomain)
        {
            await msal.initialise();
        }

        // subscribe to theme changes
        appContext().state.autoMap(this, i => ({ themeId: i.themeId }));
        appContext().state.set({ themeId: appContext().localStorageProvider.getTheme() });

        const expectedVersion = this.local.getHubVersion().split('.').slice(0, 2).join('.');
        const actualVersion = appContext().settings.internalVersion.split('.').slice(0, 2).join('.');

        if (expectedVersion != actualVersion)
        {
            this.local.setHubVersion(actualVersion);
            await appContext().authentication.logOut(LogOutReason.VersionChanged);
            return;
        }

        await this.setStateAsync({ ready: true });
        this.getNotification();
        this.notificationTimerId = setInterval(() => this.getNotification(), 120000);
    }

    public componentWillUnmount(): void
    {
        components
            .removeResizeHandler(this.resizeHandlerId)
            .dispose();

        clearInterval(this.notificationTimerId ?? undefined);
    }

    private notifications: IActiveNotification[] = [];
    private async getNotification(): Promise<void>
    {
        const token = appContext().authentication.getToken();
        if (!token)
        {
            return;
        }
        try
        {
            const notifications = await LegacyApiClient.getActiveNotifications(1);
            const numOfNewNotifications = Math.max(notifications.length - this.notifications.length, 0);
            appContext().state.set({ numOfNewNotifications: numOfNewNotifications });
            this.notifications = notifications;
        }
        catch (error)
        {
            console.log(error);
        }
    }

    public componentDidUpdate(prevProps: IProps, prevState: IState): void
    {
        if (this.state.themeId !== prevState.themeId)
        {
            this.handleThemeChange();
        }
    }

    private handleThemeChange(): void
    {
        document.documentElement.setAttribute("data-panda-theme", this.state.themeId);
        appContext().localStorageProvider.setTheme(this.state.themeId);
    }

    public render(): JSX.Element
    {
        let REACT_APP_VERSION = "";
        try
        {
            REACT_APP_VERSION = (process.env.REACT_APP_VERSION ? process.env.REACT_APP_VERSION : "");
        }
        catch (error)
        {
            REACT_APP_VERSION = "";
        }
        // This flag is true if it encounters a url passed back to the AAD popup login, after sucessful authentication. - e.g. /auth#code=xyz
        // It's used to avoid the race condition, which is between the React Router stripping the url of the hash, and the window that opened the popup grabbing the hash from url to complete login.
        const AADLoginPopup = window.location.href.includes('/auth#') && window.location.hash !== "";

        return (
            <>
                <Suspense fallback={(<div>Loading</div>)}>
                    <Helmet>
                        <meta name="version" content={REACT_APP_VERSION} />
                    </Helmet>
                    {
                        this.state.ready &&
                        <MsalProvider instance={appContext().msalProvider.msalClient}>
                            {!AADLoginPopup && <IbssRouter />}
                            {AADLoginPopup && <></>}
                        </MsalProvider>
                    }
                </Suspense>
            </>
        );
    }
}

export default App;
export interface IProps { }
export interface IState
{
    themeId: ThemeId,
    ready: boolean,
}
